import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { api } from '../utils/api'
import { Printer } from 'lucide-react'

export default function DeliveryPrintPage() {
  const { id } = useParams()

  const { data: doc, isLoading } = useQuery({
    queryKey: ['delivery', id],
    queryFn: () => api.get(`/deliveries/${id}`).then(r => r.data),
    enabled: !!id,
  })

  const { data: software = [] } = useQuery<any[]>({
    queryKey: ['delivery-software', (doc as any)?.asset?.id],
    queryFn: () => api.get(`/software/asset/${(doc as any).asset.id}`).then(r => r.data),
    enabled: !!(doc as any)?.asset?.id,
  })

  const fmt = (d: string | null | undefined) =>
    d ? new Date(d).toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''

  if (isLoading) return <div className="flex items-center justify-center h-screen text-gray-400">กำลังโหลด...</div>
  if (!doc)      return <div className="flex items-center justify-center h-screen text-gray-400">ไม่พบเอกสาร</div>

  const hardware = [
    { label: 'CPU',     value: doc.cpu     },
    { label: 'VGA',     value: doc.vga     },
    { label: 'RAM',     value: doc.ram     },
    { label: 'HDD',     value: doc.hdd     },
    { label: 'Monitor', value: doc.monitor },
  ]

  const osSoftware      = software.filter((s: any) => s.softwareCategory === 'OS')
  const generalSoftware = software.filter((s: any) => s.softwareCategory !== 'OS')

  return (
    <>
      <div className="print:hidden fixed top-4 right-4 z-50 flex gap-2">
        <button onClick={() => window.print()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium shadow-lg hover:bg-blue-700">
          <Printer size={16}/> พิมพ์ / Export PDF
        </button>
        <button onClick={() => window.close()}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm hover:bg-gray-200">
          ปิด
        </button>
      </div>

      <div className="min-h-screen bg-gray-100 print:bg-white py-8 print:py-0">
        <div className="mx-auto bg-white shadow-lg print:shadow-none"
          style={{ width: '210mm', minHeight: '297mm', padding: '15mm 15mm 10mm 15mm' }}>

          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {(doc as any).company?.logo ? (
                <img src={(doc as any).company.logo} alt="logo" className="h-14 max-w-[120px] object-contain"/>
              ) : (
                <div className="w-14 h-14 rounded-full border-2 border-gray-800 flex items-center justify-center">
                  <span className="text-sm font-bold text-gray-800">
                    {(doc as any).company?.name?.slice(0,3).toUpperCase() || 'CO'}
                  </span>
                </div>
              )}
            </div>
            <h1 className="text-xl font-bold text-center flex-1">ใบส่งมอบทรัพย์สิน</h1>
            <div className="w-14"/>
          </div>

          {/* Table 1: Machine + Employee */}
          <table className="w-full border-collapse border border-gray-800 text-xs mb-3">
            <tbody>
              <tr>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1 text-center w-24">ชื่อเครื่อง</td>
                <td className="border border-gray-800 px-2 py-1 text-center w-32">{doc.docNumber || ''}</td>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1 text-center w-28">Serial Number</td>
                <td className="border border-gray-800 px-2 py-1 text-center w-28">{doc.serialNumber || ''}</td>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1 text-center w-24">ทะเบียนทรัพย์สิน</td>
                <td className="border border-gray-800 px-2 py-1 text-center w-24">{doc.registration || ''}</td>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1 text-center" colSpan={2}>วันที่จัดซื้อ</td>
                <td className="border border-gray-800 px-2 py-1 text-center" colSpan={2}>{fmt((doc as any).asset?.purchaseDate || doc.contractStart)}</td>
              </tr>
              <tr>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1 text-center">รหัสพนักงาน</td>
                <td className="border border-gray-800 px-2 py-1 text-center">{doc.employeeId || ''}</td>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1 text-center" colSpan={2}>ชื่อ - นามสกุล</td>
                <td className="border border-gray-800 px-2 py-1 text-center" colSpan={2}>{doc.employeeName || ''}</td>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1 text-center" colSpan={2}>สถานที่</td>
                <td className="border border-gray-800 px-2 py-1 text-center" colSpan={2}>{doc.location || ''}</td>
              </tr>
            </tbody>
          </table>

          {/* Model */}
          {doc.brand && (
            <table className="w-full border-collapse border border-gray-800 text-xs mb-3">
              <tbody>
                <tr>
                  <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1 w-24">Model</td>
                  <td className="border border-gray-800 px-2 py-1">{doc.brand}</td>
                </tr>
              </tbody>
            </table>
          )}

          {/* Table: Hardware specs */}
          <table className="w-full border-collapse border border-gray-800 text-xs mb-3">
            <thead>
              <tr>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1 text-center w-20">รายการ</td>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1 text-center w-32">Serial Number</td>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1 text-center w-32">รหัสทรัพย์สิน</td>
                <td className="border border-gray-800 bg-gray-100 font-bold px-2 py-1 text-center">รายละเอียด</td>
              </tr>
            </thead>
            <tbody>
              {hardware.map(({ label, value }, i) => (
                <tr key={label}>
                  <td className="border border-gray-800 bg-gray-50 font-bold px-2 py-1.5">{label}</td>
                  <td className="border border-gray-800 px-2 py-1.5"/>
                  <td className="border border-gray-800 px-2 py-1.5 text-center text-gray-600">
                    {i === 0 ? doc.registration || '' : ''}
                  </td>
                  <td className="border border-gray-800 px-2 py-1.5">{value || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Table: Software */}
          {software.length > 0 && (
            <table className="w-full border-collapse border border-gray-800 text-xs mb-4">
              <thead>
                <tr>
                  <td colSpan={3} className="border border-gray-800 bg-gray-100 font-bold px-2 py-1 text-center">
                    รายการ Software
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-800 bg-gray-50 font-bold px-2 py-1 text-center w-20">ประเภท</td>
                  <td className="border border-gray-800 bg-gray-50 font-bold px-2 py-1 text-center">ชื่อโปรแกรม</td>
                  <td className="border border-gray-800 bg-gray-50 font-bold px-2 py-1 text-center w-20">Version</td>
                </tr>
              </thead>
              <tbody>
                {osSoftware.map((sw: any) => (
                  <tr key={sw.id}>
                    <td className="border border-gray-800 px-2 py-1.5 text-center font-medium">OS</td>
                    <td className="border border-gray-800 px-2 py-1.5">{sw.name}</td>
                    <td className="border border-gray-800 px-2 py-1.5 text-center">{sw.version || ''}</td>
                  </tr>
                ))}
                {generalSoftware.map((sw: any) => (
                  <tr key={sw.id}>
                    <td className="border border-gray-800 px-2 py-1.5 text-center text-gray-500">App</td>
                    <td className="border border-gray-800 px-2 py-1.5">{sw.name}</td>
                    <td className="border border-gray-800 px-2 py-1.5 text-center">{sw.version || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Legal text */}
          <div className="text-xs text-gray-700 mb-6 space-y-1">
            <p className="underline">ความเสียหายที่เกิดขึ้นกับทรัพย์สินทุกอย่างที่อยู่ในรายการนี้ หากอยู่นอกเหนือการรับประกันของชิ้นส่วนหรือเกิดการสูญหายพนักงาน ผู้เป็นเจ้าของจะต้องรับผิดชอบต่อค่าเสียหายทั้งหมด</p>
            <p className="underline">{(doc as any).company?.name || 'บริษัท'} จะรับผิดชอบรายการ Software ที่ได้ระบุไว้เท่านั้น ในกรณีที่พบ Software ละเมิดสิขสิทธิ์พนักงานผู้มีชื่อเป็นเจ้าของเครื่องจ้องรับผิดชอบต่อคามผิดทั้งหมด ทางบริษัทไม่มีส่วนรับผิดชอบต่อคดีความที่เกิดขึ้นในทุกกรณี</p>
          </div>

          {/* Signatures */}
          <div className="grid grid-cols-2 gap-16 text-xs mt-4">
            <div>
              <p>ผู้รับมอบเครื่อง............................................</p>
              <p className="mt-3">วันที่ .......... / .......... / ..........</p>
            </div>
            <div>
              <p>ผู้จัดการฝ่าย ................................................</p>
              <p className="mt-3">วันที่ .......... / .......... / ..........</p>
            </div>
          </div>

        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          body { margin: 0; }
        }
      `}</style>
    </>
  )
}
